// @flow 
import * as React from 'react';
import Stack from "@mui/material/Stack";
import { Checkbox, FormControl, FormControlLabel, FormHelperText } from "@mui/material";
import ColorSelect from "../badgeForm/printable/colorSelect/colorSelect";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import type { BadgeTemplateTypes } from "../../../types/badge/types";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { EXHIBITION } from "../../../locales/components/namespaces";
import ConfirmDialog from "../dialogs/confirmDialog/confirmDialog";

type Props = {
	onClose: Function,
	badgeTemplateTypes: Array<BadgeTemplateTypes>
};

const Form = (props: Props) => {
	const {
		badgeTemplateTypes,
		onClose,
		open,
		initialValues,
		onSubmit,
		title
	} = props
	const { t } = useTranslation(EXHIBITION)
	
	const validationSchema = yup.object({
		textColor: yup
		.string()
		.required(t('required')),
		backgroundColor: yup
		.string()
		.required(t('required')),
		badgeTemplateTypeId: yup
		.string()
		.required(t('required')),
		regBadgeTemplateTypeId: yup
		.string()
		.required(t('required')),
		text: yup.mixed().test(
			'acceptedValues', t('required'), function (value) {
				return value && value?.en && value?.el;
			}
		),
	})
	
	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	return (
		<ConfirmDialog
			maxWidth={"sm"}
			open={open}
			handleDialog={onClose}
			title={title}
			otherDialogProps={{ keepMounted: true }}
			onNo={onClose}
			onYes={formik.handleSubmit}
			hasBtnIcons={false}
		>
			<FormikProvider value={formik}>
				<Box component="form" onSubmit={formik.handleSubmit} noValidate>
					<Stack direction={"row"} spacing={6} sx={{ mb: 1.5 }}>
						<FormControl
							variant="standard"
							fullWidth
							error={
								formik.touched["backgroundColor"] &&
								Boolean(formik.errors["backgroundColor"])
							}
						>
							<FormControlLabel
								control={
									<ColorSelect
										color={formik.values.backgroundColor}
										onColorChange={(color) => formik.setFieldValue(`backgroundColor`, color)}
									/>
								}
								label={t('Background Color')}
								labelPlacement="start"
							/>
							<FormHelperText>
								<ErrorMessage name={"backgroundColor"}/>
							</FormHelperText>
						</FormControl>
						<FormControl
							variant="standard"
							fullWidth
							error={
								formik.touched["textColor"] &&
								Boolean(formik.errors["textColor"])
							}
						>
							<FormControlLabel
								control={
									<ColorSelect
										color={formik.values.textColor}
										onColorChange={(color) => formik.setFieldValue(`textColor`, color)}
									/>
								}
								label={t('Text Color')}
								labelPlacement="start"
							/>
							<FormHelperText>
								<ErrorMessage name={"textColor"}/>
							</FormHelperText>
						</FormControl>
					</Stack>
					<FormControl
						variant="outlined"
						fullWidth
						error={
							formik.touched["badgeTemplateTypeId"] &&
							Boolean(formik.errors["badgeTemplateTypeId"])
						}
						sx={{ mb: 2 }}
					>
						<TextField
							select
							name={"badgeTemplateTypeId"}
							value={formik.values.badgeTemplateTypeId}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							label={t('Print Size')}
							fullWidth
							size={"small"}
							variant={'filled'}
							slotProps={{
								inputLabel: {
									shrink: true,
								},
							}}
						>
							{badgeTemplateTypes.map(item => (
								<MenuItem key={item.id} value={item.id}>{item.description}</MenuItem>
							))}
						</TextField>
						<FormHelperText>
							<ErrorMessage name={"badgeTemplateTypeId"}/>
						</FormHelperText>
					</FormControl>
					<FormControl
						variant="outlined"
						fullWidth
						error={
							formik.touched["regBadgeTemplateTypeId"] &&
							Boolean(formik.errors["regBadgeTemplateTypeId"])
						}
						sx={{ mb: 2 }}
					>
						<TextField
							select
							name={"regBadgeTemplateTypeId"}
							value={formik.values.regBadgeTemplateTypeId}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							label={t('Registration Print Size')}
							fullWidth
							size={"small"}
							variant={'filled'}
							slotProps={{
								inputLabel: {
									shrink: true,
								},
							}}
						>
							{badgeTemplateTypes.map(item => (
								<MenuItem key={item.id} value={item.id}>{item.description}</MenuItem>
							))}
						</TextField>
						<FormHelperText>
							<ErrorMessage name={"regBadgeTemplateTypeId"}/>
						</FormHelperText>
					</FormControl>
					<FormControl
						variant="standard"
						fullWidth
						error={
							formik.touched["text"] &&
							Boolean(formik.errors["text"])
						}
						sx={{ mb: 2 }}
					>
						<TextField
							id="outlined-name"
							label={t('Greek Category Description')}
							value={formik.values.text.el}
							name={`text.el`}
							variant={'filled'}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							size={'small'}
							required
							slotProps={{
								htmlInput: {
									maxLength: 150
								},
							}}
						/>
						<FormHelperText>
							<ErrorMessage name={"text"}/>
						</FormHelperText>
					</FormControl>
					<FormControl
						variant="standard"
						fullWidth
						error={
							formik.touched["text"] &&
							Boolean(formik.errors["text"])
						}
						sx={{ mb: 2 }}
					>
						<TextField
							id="outlined-name"
							label={t('English Category Description')}
							value={formik.values.text.en}
							name={`text.en`}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							variant={'filled'}
							size={'small'}
							required
							slotProps={{
								htmlInput: { maxLength: 150 }
							}}
						/>
						<FormHelperText>
							<ErrorMessage name={"text"}/>
						</FormHelperText>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<TextField
							id="outlined-name"
							label={t('Greek Category Explanation')}
							value={formik.values.explanation.el}
							name={`explanation.el`}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							size={'small'}
							variant={'filled'}
							sx={{ mb: 2 }}
							slotProps={{
								htmlInput: {
									maxLength: 150
								},
							}}
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<TextField
							id="outlined-name"
							label={t('English Category Explanation')}
							value={formik.values.explanation.en}
							name={`explanation.en`}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							fullWidth
							size={'small'}
							variant={'filled'}
							sx={{ mb: 2 }}
							slotProps={{
								htmlInput: {
									maxLength: 150
								},
							}}
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={formik.values.displayVisitorLabelInBadge}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									size={'small'}
									name={'displayVisitorLabelInBadge'}
								/>
							}
							label={t('Display Visitor Label In Badge')}
							labelPlacement="end"
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={formik.values.displayBadgeInEnglish}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									size={'small'}
									name={'displayBadgeInEnglish'}
								/>
							}
							label={t('Display Badge Only In English')}
							labelPlacement="end"
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={formik.values.showPreregistration}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									size={'small'}
									name={'showPreregistration'}
								/>
							}
							label={t('Visible In Preregistration')}
							labelPlacement="end"
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={formik.values.showOrganizer}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									size={'small'}
									name={'showOrganizer'}
								/>
							}
							label={t('Visible To Organizer')}
							labelPlacement="end"
						/>
					</FormControl>
				</Box>
			</FormikProvider>
		</ConfirmDialog>
	);
};

export default Form